/*
 * ************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *   Copyright 2020 Adobe Systems Incorporated
 *   All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by all applicable intellectual property
 * laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 * ************************************************************************
 */

import * as R from 'ramda';
import React from 'react';
import classnames from 'classnames';
import Button from '@react/react-spectrum/Button';
import Icon from '@react/react-spectrum/Icon';
import InfoOutline from '@react/react-spectrum/Icon/InfoOutline';
import OverlayTrigger from '@react/react-spectrum/OverlayTrigger';
import { SideNav, SideNavHeading, SideNavItem } from '@react/react-spectrum/SideNav';
import Tooltip from '@react/react-spectrum/Tooltip';

import styles from './styles/plugin.view.nav.list.styl';

const SideRailItem = ({
  description,
  displayName,
  icon,
  isSelected,
  showInfoIcon = true,
  toggleIcon,
  ...props
}) => (
  <div
    className={styles.sideNavItemWrapper}
  >
    <SideNavItem
      className={classnames(
        { 'is-selected': isSelected },
        toggleIcon && 'withButton'
      )}
      icon={icon ? <Icon size="S"><img alt={displayName} src={icon} /></Icon> : null}
      label={displayName}
      {...props}
    />
    <div className={styles.toggleIcon}>
      {toggleIcon}
    </div>
    {showInfoIcon && description && (
      <OverlayTrigger
        placement="bottom"
        trigger="click"
      >
        <Button
          className={styles.infoIcon}
          variant="action"
          quiet
          type="button"
          icon={<InfoOutline />}
        />
        <Tooltip>{description}</Tooltip>
      </OverlayTrigger>
    )}
  </div>
);

const SideRailList = ({
  onSelectView,
  plugins,
  itemPropsFunc,
  selectedView
}) => {
  if (!plugins.length) { return null; }
  return (
    <div className={styles.railPluginList}>
      <SideNav
        onSelect={onSelectView}
        value=""
      >
        {!R.isEmpty(plugins) && R.map((groupPlugins) => {
          const category = R.path([0, 'category'], groupPlugins);

          return (
            <SideNavHeading
              className="heading"
              key={category}
              label={category}
            >
              {R.map(({ description, displayName, icon, namespace }) => {
                const isSelected = selectedView && selectedView === namespace;
                const itemProps = itemPropsFunc ? itemPropsFunc(namespace) : null;

                return (
                  <SideRailItem
                    isSelected={isSelected}
                    icon={icon}
                    key={namespace}
                    description={description}
                    displayName={displayName}
                    value={namespace}
                    {...itemProps}
                  />
                );
              }, groupPlugins)}
            </SideNavHeading>
          );
        }, plugins)}
      </SideNav>
    </div>
  );
};

SideRailList.displayName = 'SideRailList';

export default SideRailList;
